import { useAuth } from '../contexts/AuthContext.jsx';
import { useReducer } from 'react';
import { useJobStatus } from '../contexts/JobStatusContext.jsx';
import { useFetchMutation } from './useFetchAuth.js';
import { useToast } from '../components/ToastContainer.jsx';

const initialState = {
  // Navigation states
  currentStep: 1,
  completedSteps: new Set(),

  // Select file States
  fileName: null,
  // The name the History will show: fileName is the upload's temp folder, with its id
  excelName: null,
  sheets: null,
  shopSign: null,
  client: null,
  year: null,
  week: null,
  uploadedFile: null,
  category: null,

  // Select Config States
  sheetConfigs: {},
  mappingChoices: [],
  mappings: [],
  isUsingExistingMapping: false,
  selectedMapping: null,

  // SUBMIT PROCESS States
  latestJob: null,
  error: null,
};

function processorReducer(state, action) {
  switch (action.type) {
    case 'RESET_ALL':
      return { ...initialState };

    case 'UPLOAD_SUCCESS': {
      const data = action.payload;
      const hasMappings = data.mappings && data.mappings.length > 0;
      // If mappings exist, go to step 2, otherwise step 3
      const nextStep = hasMappings ? 2 : 3;
      
      // Mark step 1 as completed
      const newCompletedSteps = new Set([1]);

      return {
        ...state,
        fileName: data.file_name,
        excelName: data.excel_name,
        sheets: data.sheets,
        shopSign: data.shopSign,
        client: data.client,
        year: data.year,
        week: data.week,
        category: data.category,
        uploadedFile: data.file || state.uploadedFile,
        mappings: data.mappings || [],
        currentStep: nextStep,
        completedSteps: newCompletedSteps,
      };
    }

    case 'SET_STEP': {
      const { step, completedUpTo } = action.payload;
      let newCompletedSteps = state.completedSteps;
      
      if (completedUpTo) {
        newCompletedSteps = new Set([...Array(completedUpTo)].map((_, i) => i + 1));
      }

      return {
        ...state,
        currentStep: step,
        completedSteps: newCompletedSteps,
      };
    }

    case 'GO_BACK': {
      const { hasMappings } = action.payload;
      const currentStep = state.currentStep;
      let newStep = currentStep;
      const newCompletedSteps = new Set(state.completedSteps);

      if (currentStep === 2) {
        newStep = 1;
        newCompletedSteps.delete(2);
      } else if (currentStep === 3) {
        if (hasMappings) {
          newStep = 2;
        } else {
          newStep = 1;
        }
        newCompletedSteps.delete(3);
      } else if (currentStep === 4) {
        newStep = 3;
        newCompletedSteps.delete(4);
      }

      return {
        ...state,
        currentStep: newStep,
        completedSteps: newCompletedSteps,
      };
    }

    case 'SELECT_MAPPING': {
      const { savedMapping, newSheetConfigs } = action.payload;
      const newCompletedSteps = new Set(state.completedSteps);
      newCompletedSteps.add(2);

      return {
        ...state,
        selectedMapping: savedMapping,
        sheetConfigs: newSheetConfigs,
        isUsingExistingMapping: true,
        currentStep: 3,
        completedSteps: newCompletedSteps,
      };
    }

    case 'CONTINUE_MANUAL': {
      const newCompletedSteps = new Set(state.completedSteps);
      newCompletedSteps.add(2);
      
      return {
        ...state,
        isUsingExistingMapping: false,
        currentStep: 3,
        completedSteps: newCompletedSteps,
      };
    }

    case 'UPDATE_MAPPING':
      return {
        ...state,
        sheetConfigs: action.payload.sheetConfigs,
        mappingChoices: action.payload.choices,
        isUsingExistingMapping: false,
      };
    
    case 'DELETE_MAPPING':
      return {
        ...state,
        mappings: state.mappings.filter(m => m.mapping_id !== action.payload),
      };

    case 'SUBMIT_INIT':
      return {
        ...state,
        error: null,
        latestJob: null,
      };

    case 'SUBMIT_SUCCESS':
      return {
        ...state,
        latestJob: action.payload,
      };

    case 'SUBMIT_ERROR':
      return {
        ...state,
        error: action.payload,
      };

    default:
      return state;
  }
}

export function useProcessorState() {
  const { addJob } = useJobStatus();
  const { mutateAsync: deleteMapping } = useFetchMutation();
  const { addToast } = useToast();
  const { mutateAsync: submitProcessReq } = useFetchMutation();

  const [state, dispatch] = useReducer(processorReducer, initialState);


  const handleUploadSuccess = (data) => {
    dispatch({ type: 'UPLOAD_SUCCESS', payload: data });
  };

  const goToStep = (step, completedUpTo) => {
    dispatch({ type: 'SET_STEP', payload: { step, completedUpTo } });
  };

  const handleBack = (currentStep, hasMappings) => {
    dispatch({ type: 'GO_BACK', payload: { hasMappings } });
  };

  const handleMappingSelect = (savedMapping) => {
    const newSheetConfigs = {};
    savedMapping.sheets.forEach(({ sheet, mapping }) => {
      newSheetConfigs[sheet] = {
        isProcessing: true,
        columnMappings: mapping,
      };
    });

    dispatch({
      type: 'SELECT_MAPPING',
      payload: { savedMapping, newSheetConfigs },
    });

    return newSheetConfigs;
  };

  const handleContinueManual = () => {
    dispatch({ type: 'CONTINUE_MANUAL' });
  };

  const handleMappingUpdate = (sheetConfigs, choices) => {
    dispatch({
      type: 'UPDATE_MAPPING',
      payload: { sheetConfigs, choices },
    });
  };

  const handleNext = (sheetConfigs, choices) => {
    handleMappingUpdate(sheetConfigs, choices);
    goToStep(4, 3);
  };

  const handleRestart = () => {
    dispatch({ type: 'RESET_ALL' });
  };

  // The mapping leaves the list only once the server deleted it: a refused delete used to hide
  // it anyway, with the error only in the console
  const handleDeleteMapping = async (mappingId) => {
    try {
      await deleteMapping({ url: `/api/delete-mapping/${mappingId}/`, method: 'DELETE' });
      dispatch({ type: 'DELETE_MAPPING', payload: mappingId });
    } catch (error) {
      addToast({ title: 'Suppression impossible', message: error.message, type: 'error', duration: 8000 });
    }
  };

  const submitProcess = async (fileName, config, selectedMapping, shopSign, client, year, week, category, confirmedAnomalies = []) => {
    console.log('submitProcess', fileName, config, selectedMapping, shopSign, client, year, week, category);

    const mappings_to_remove = ["_client", "_shop_sign", "_year", "_week"];
    const sheetsArray = Object.keys(config).map((sheetName) => ({
      sheet: sheetName,
      mapping: Object.entries(config[sheetName].columnMappings || {})
        .filter(([key]) => !mappings_to_remove.includes(key))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
      isProcessing: config[sheetName].isProcessing,
    }));

    const payload = {
      shop_sign: shopSign,
      client: client,
      year: year,
      week: week,
      sheets: sheetsArray,
      mapping_id: selectedMapping?.mapping_id || null,
      category: category || 'default',
      confirmed_anomalies: confirmedAnomalies
    };

    try {
      dispatch({ type: 'SUBMIT_INIT' });

      const responseData = await submitProcessReq({
        url: `/api/process/${fileName}/`,
        method: 'POST',
        body: payload
      });

      const jobId = responseData?.job_id;
      const jobPayload = responseData?.job_payload || null;

      if (!jobId) {
        dispatch({ type: 'SUBMIT_ERROR', payload: 'Réponse invalide du serveur' });
        return false;
      }

      addJob(jobId, jobPayload || {
        file_name: fileName,
        shop_sign: shopSign,
        client,
        year,
        week,
        category: category || 'default'
      });

      dispatch({
        type: 'SUBMIT_SUCCESS',
        payload: {
          id: jobId,
          payload: jobPayload,
          message: responseData?.message || 'Le traitement a été planifié.'
        }
      });

      return true;
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error?.response?.detail || error?.message || 'Erreur lors de l\'importation';
      dispatch({ type: 'SUBMIT_ERROR', payload: errorMessage });
      return false;
    }
  };

  const handleSubmit = async (config = state.sheetConfigs, selectedMapping = null, confirmedAnomalies = []) => {
    console.log('handleSubmit', selectedMapping);

    const success = await submitProcess(
      state.fileName,
      config,
      selectedMapping,
      state.shopSign,
      state.client,
      state.year,
      state.week,
      state.category,
      confirmedAnomalies
    );

    if (success) {
      goToStep(5, 4);
    }
  };

  // API Compatibility Return
  return {
    state: {
      ...state,
      selectedMapping: state.selectedMapping,
      error: state.error,
    },
    actions: {
      handleUploadSuccess,
      handleBack,
      handleMappingSelect,
      handleContinueManual,
      handleNext,
      handleSubmit,
      handleRestart,
      handleDeleteMapping,
    },
  };
}
